'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useThrottle } from './useThrottle';

export interface Position {
  top: number;
  bottom: number;
  scrollY: number;
  scrollX: number;
  height: number;
  width: number;
}

export interface UseElementPositionOptions {
  throttleMs?: number;
  useResizeObserver?: boolean;
  mutationObserverOptions?: MutationObserverInit;
}

export const useElementPosition = (elementId: string, options: UseElementPositionOptions = {}): Position => {
  const {
    mutationObserverOptions = {
      attributeFilter: ['style', 'class'],
      attributes: true,
      childList: false,
      subtree: false,
    },
    throttleMs = 100,
    useResizeObserver = true,
  } = options;

  const [position, setPosition] = useState<Position>({
    bottom: 0,
    height: 0,
    scrollX: 0,
    scrollY: 0,
    top: 0,
    width: 0,
  });

  const rafIdRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef(false);

  const updatePosition = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const element = document.getElementById(elementId);
      if (element) {
        const rect = element.getBoundingClientRect();
        setPosition({
          bottom: rect.bottom,
          height: rect.height,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
          top: rect.top,
          width: rect.width,
        });
      }
      pendingUpdateRef.current = false;
      rafIdRef.current = null;
    });
  }, [elementId]);

  const [throttledScrollUpdate] = useThrottle(updatePosition, throttleMs);
  const [throttledResizeUpdate] = useThrottle(updatePosition, throttleMs * 2);

  useEffect(() => {
    updatePosition();

    const element = document.getElementById(elementId);
    if (!element) return;

    window.addEventListener('scroll', throttledScrollUpdate, { passive: true });
    window.addEventListener('resize', throttledResizeUpdate);
    window.addEventListener('orientationchange', updatePosition);

    const mutationObserver = new MutationObserver(() => {
      if (!pendingUpdateRef.current) {
        pendingUpdateRef.current = true;
        updatePosition();
      }
    });
    mutationObserver.observe(element, mutationObserverOptions);

    return () => {
      window.removeEventListener('scroll', throttledScrollUpdate);
      window.removeEventListener('resize', throttledResizeUpdate);
      window.removeEventListener('orientationchange', updatePosition);
      mutationObserver.disconnect();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [
    elementId,
    updatePosition,
    throttledScrollUpdate,
    throttledResizeUpdate,
    mutationObserverOptions,
    useResizeObserver,
  ]);

  return position;
};

export default useElementPosition;
